/**
 * Create / edit page for an account-takeover rule, modelled on the vendor's
 * "Add Failed login rule" form. Reached from the list via
 * `account-takeover/new?domain=&category=` and `.../edit?...&id=`.
 *
 * The structured form maps to the backend `atpRuleRequest` (`action` + match
 * `conditions`) — see `buildInput` / `parseRule` below. Condition field tokens
 * and the action enum are best-guess from the design; centralised here so they
 * are easy to adjust against live vendor/template data.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafATPRules,
  useWafSecurityTemplates,
  useCreateATPRule,
  useUpdateATPRule,
  wafExtractError,
  type ATPCategory,
  type ATPAction,
  type ATPCondition,
  type AccountTakeoverRule,
  type AccountTakeoverRuleInput,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import styles from './WafAccountTakeover.module.css';

/** Condition field tokens (vendor `<NAME>:match_all`). */
const FIELD = {
  path: 'URI:match_all',
  method: 'REQUEST_METHOD:match_all',
  code: 'RESPONSE_CODE:match_all',
  body: 'RESPONSE_BODY:match_all',
  header: 'RESPONSE_HEADERS:match',
};

/** Operators offered for the response-header condition (from the design dropdown). */
const HEADER_OPERATORS = ['streq', 'contains', 'within', 'prefix', 'suffix', 'include'];

/** Display operator → vendor operator token (only the ones that differ). */
const HEADER_OP_SEND: Record<string, string> = { prefix: 'beginsWith', suffix: 'endsWith', include: 'pm' };
const HEADER_OP_DISPLAY: Record<string, string> = Object.fromEntries(
  Object.entries(HEADER_OP_SEND).map(([k, v]) => [v, k]),
);

/** Action segmented controls (failed login vs credential stuffing differ). */
const FL_ACTIONS = [
  { value: 'log', label: 'Log' },
  { value: 'block', label: 'Block' },
  { value: 'rateLimit', label: 'Rate-Limit' },
];
const CS_ACTIONS = [
  { value: 'log', label: 'Log' },
  { value: 'warningHeader', label: 'Warning header' },
];

/** Segmented value → vendor action token. */
const FL_ACTION_SEND: Record<string, string> = { log: 'Log', block: 'Block', rateLimit: 'Rate-Limit' };
const CS_ACTION_SEND: Record<string, string> = { log: 'Log', warningHeader: 'Warning header' };

/** Credential-stuffing payload type + its condition field token (tentative). */
const PAYLOAD_OPTIONS = [
  { value: 'JSON', label: 'JSON' },
  { value: 'FORM', label: 'Form data' },
];
/** Payload type → Content-Type, sent as a `REQUEST_HEADERS:match` condition on content-type. */
const PAYLOAD_HEADER_FIELD = 'REQUEST_HEADERS:match';
const PAYLOAD_CONTENT: Record<string, string> = {
  JSON: 'application/json',
  FORM: 'application/x-www-form-urlencoded',
};
const contentToPayload = (v: string) => (v.toLowerCase().includes('json') ? 'JSON' : 'FORM');

const HTTP_METHODS: MultiSelectOption[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'].map((m) => ({ value: m, label: m }));

const HTTP_CODES: MultiSelectOption[] = [
  100, 101, 102, 103, 200, 201, 202, 204, 206, 301, 302, 303, 304, 307, 308,
  400, 401, 402, 403, 404, 405, 406, 408, 409, 410, 412, 413, 415, 422, 429,
  500, 501, 502, 503, 504,
].map((c) => ({ value: String(c), label: String(c) }));

const CHECK_TIME = 60; // "/ min." — the detection window is per minute.

const splitCsv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

type FieldSlot = 'path' | 'method' | 'code' | 'body' | 'header' | 'payload';

/** Classify an existing condition's field token back into a form slot. */
function classifyField(field: string): FieldSlot | null {
  const f = field.toLowerCase();
  if (f.includes('payload')) return 'payload';
  // request headers (content-type) carry the payload type; response headers are a match field.
  if (f.includes('header')) return f.includes('request') ? 'payload' : 'header';
  if (f.includes('body')) return 'body';
  if (f.includes('code') || f.includes('status')) return 'code';
  if (f.includes('method')) return 'method';
  if (f.includes('uri') || f.includes('path') || f.includes('url')) return 'path';
  return null;
}

/** Failed-login action token → segmented value. */
function normalizeAction(v: string | undefined): string {
  const t = (v ?? '').toLowerCase();
  if (t.includes('rate')) return 'rateLimit';
  if (t.includes('block')) return 'block';
  return 'log';
}

/** Credential-stuffing action token → segmented value. */
function normalizeCCAction(v: string | undefined): string {
  return (v ?? '').toLowerCase().includes('warn') ? 'warningHeader' : 'log';
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint ? <span className={styles.subHint}>{hint}</span> : null}
    </div>
  );
}

function NumberWithUnit({ value, onChange, unit, disabled, placeholder }: { value: string; onChange: (v: string) => void; unit: string; disabled: boolean; placeholder?: string }) {
  return (
    <div className={styles.suffixWrap}>
      <Input type="number" min={0} className={styles.noSpin} value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      <span className={styles.suffix}>{unit}</span>
    </div>
  );
}

export function WafAccountTakeoverForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const category = (params.get('category') === 'credential_stuffing' ? 'credential_stuffing' : 'failed_login') as ATPCategory;
  const id = params.get('id');
  const isEdit = !!id;
  const isFL = category === 'failed_login';

  const back = () => navigate(`/services/waf/${serviceId}/account-takeover`);

  const rulesQ = useWafATPRules(serviceId, isEdit ? domain : undefined, isEdit ? category : undefined);
  const rule = useMemo<AccountTakeoverRule | undefined>(
    () => (isEdit ? (rulesQ.data ?? []).find((r) => r.id === id) : undefined),
    [isEdit, rulesQ.data, id],
  );

  // Create + ?template= → clone that template's action/conditions into the form.
  const templateId = params.get('template') ?? '';
  const wantTemplate = !isEdit && !!templateId;
  const templatesQ = useWafSecurityTemplates(serviceId, wantTemplate ? category : undefined);
  const template = useMemo(
    () => (wantTemplate ? (templatesQ.data ?? []).find((t) => t.id === templateId) : undefined),
    [wantTemplate, templatesQ.data, templateId],
  );

  // ---- form state ----
  const [seeded, setSeeded] = useState(false);
  const [name, setName] = useState('');
  const [path, setPath] = useState('');
  const [methods, setMethods] = useState<string[]>([]);
  const [maxLogin, setMaxLogin] = useState('');
  const [maxFailed, setMaxFailed] = useState('');
  const [usernameExpr, setUsernameExpr] = useState('');
  const [passwordExpr, setPasswordExpr] = useState('');
  const [payloadType, setPayloadType] = useState('JSON');
  const [respCode, setRespCode] = useState(false);
  const [respBody, setRespBody] = useState(false);
  const [respHeader, setRespHeader] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [bodyKeywords, setBodyKeywords] = useState('');
  const [headerName, setHeaderName] = useState('');
  const [headerOp, setHeaderOp] = useState('within');
  const [headerValue, setHeaderValue] = useState('');
  const [action, setAction] = useState(isFL ? 'block' : 'log');
  const [duration, setDuration] = useState('60');

  // Seed once (from `rule` on edit, a `template` on create, else blank).
  useEffect(() => {
    if (seeded) return;
    if (isEdit) {
      if (rulesQ.isLoading) return;
      if (rule) seedFrom(rule.name, rule.action, rule.conditions);
      setSeeded(true);
    } else if (wantTemplate) {
      if (templatesQ.isLoading) return;
      if (template) seedFrom(template.name, template.action, template.conditions);
      setSeeded(true);
    } else {
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, isEdit, rule, rulesQ.isLoading, wantTemplate, template, templatesQ.isLoading]);

  function seedFrom(nameV: string, a: ATPAction, conds: ATPCondition[]) {
    setName(nameV);
    if (isFL) {
      setMaxFailed(a.req_count != null ? String(a.req_count) : '');
      setMaxLogin(a.surge_defend_num != null ? String(a.surge_defend_num) : '');
      setAction(normalizeAction(a.fl_action));
      setDuration(String(a.fl_block_time ?? a.surge_lock_time ?? 60));
    } else {
      setUsernameExpr(a.username_expression ?? '');
      setPasswordExpr(a.password_expression ?? '');
      setAction(normalizeCCAction(a.cc_action));
    }
    for (const c of conds ?? []) {
      switch (classifyField(c.field)) {
        case 'path': setPath(c.value ?? ''); break;
        case 'payload': if (c.value) setPayloadType(contentToPayload(c.value)); break;
        case 'method': setMethods(splitCsv(c.value ?? '')); break;
        case 'code': setRespCode(true); setCodes(splitCsv(c.value ?? '')); break;
        case 'body': setRespBody(true); setBodyKeywords(c.value ?? ''); break;
        case 'header': setRespHeader(true); setHeaderName(c.key ?? ''); setHeaderOp(HEADER_OP_DISPLAY[c.operator] ?? c.operator ?? 'within'); setHeaderValue(c.value ?? ''); break;
        default: break;
      }
    }
  }

  const clearCode = () => { setRespCode(false); setCodes([]); };
  const clearBody = () => { setRespBody(false); setBodyKeywords(''); };
  const clearHeader = () => { setRespHeader(false); setHeaderName(''); setHeaderValue(''); };

  const create = useCreateATPRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule created' }); trackFeatureAction('waf', 'atp_rule', 'create'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateATPRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule updated' }); trackFeatureAction('waf', 'atp_rule', 'edit'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  function buildConditions(): ATPCondition[] {
    const conds: ATPCondition[] = [];
    if (path.trim()) conds.push({ field: FIELD.path, key: '', operator: 'contains', value: path.trim() });
    if (!isFL) {
      if (payloadType) conds.push({ field: PAYLOAD_HEADER_FIELD, key: 'content-type', operator: 'streq', value: PAYLOAD_CONTENT[payloadType] ?? PAYLOAD_CONTENT.JSON });
      return conds;
    }
    if (methods.length) conds.push({ field: FIELD.method, key: '', operator: 'pm', value: methods.join(',') });
    if (respCode && codes.length) conds.push({ field: FIELD.code, key: '', operator: 'pm', value: codes.join(',') });
    if (respBody && bodyKeywords.trim()) conds.push({ field: FIELD.body, key: '', operator: 'contains', value: bodyKeywords.trim() });
    if (respHeader && headerName.trim()) conds.push({ field: FIELD.header, key: headerName.trim(), operator: HEADER_OP_SEND[headerOp] ?? headerOp, value: headerValue.trim() });
    return conds;
  }

  function buildAction(): ATPAction {
    if (isFL) {
      // Log hides the Duration field but still defaults block/lock to 60.
      const block = Number(duration) || 60;
      return {
        type: 'failedLogin',
        req_count: Number(maxFailed) || 0,
        fl_check_time: CHECK_TIME,
        fl_action: FL_ACTION_SEND[action] ?? action,
        fl_block_time: block,
        surge_defend: !!maxLogin.trim(),
        surge_defend_num: Number(maxLogin) || 0,
        surge_lock_time: block,
      };
    }
    return {
      type: 'credentialStuffing',
      username_expression: usernameExpr.trim(),
      password_expression: passwordExpr.trim(),
      cc_action: CS_ACTION_SEND[action] ?? action,
    };
  }

  const originSelected = (respCode && codes.length > 0) || (respBody && !!bodyKeywords.trim()) || (respHeader && !!headerName.trim());
  const conditions = buildConditions();
  const valid = isFL
    ? !!name.trim() && !!path.trim() && !!maxFailed.trim() && originSelected && conditions.length > 0
    : !!name.trim() && !!path.trim() && !!usernameExpr.trim() && !!passwordExpr.trim();

  const submit = () => {
    if (!valid) return;
    const input: AccountTakeoverRuleInput = {
      name: name.trim(),
      enable: rule ? rule.enable : true,
      action: buildAction(),
      conditions,
    };
    if (isEdit && rule) update.mutate({ domain, category, id: rule.id, input });
    else create.mutate({ domain, category, input });
  };

  const header = (
    <PageHeader
      icon={<ShieldAlert size={22} />}
      title="Account takeover prevention"
      description="Prevent accesses and monitor that fraudulently take over account information."
      actions={<Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={back}>Back</Button>}
    />
  );

  if (!domain) {
    return (
      <Container size="lg">{header}
        <Alert tone="warning" title="No domain selected" actions={<button onClick={back}>Back</button>}>
          Open this form from the Account Takeover list so a domain is selected.
        </Alert>
      </Container>
    );
  }
  if ((isEdit || wantTemplate) && !seeded) {
    return <Container size="lg">{header}<Skeleton style={{ height: 420 }} /></Container>;
  }
  if (isEdit && seeded && !rule) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Rule not found" actions={<button onClick={back}>Back</button>}>This rule no longer exists.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      {header}
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit' : 'Add'} {isFL ? 'Failed login' : 'Credential stuffing'} rule</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Rule name">
              <Input value={name} placeholder="rule" disabled={pending} onChange={(e) => setName(e.target.value)} />
            </Field>

            {isFL ? (
              <>
                <hr className={styles.divider} />
                <p className={styles.sectionTitle}>If incoming requests match…</p>

                <div className={styles.grid2}>
                  <Field label="Path">
                    <Input value={path} placeholder="/login" disabled={pending} onChange={(e) => setPath(e.target.value)} />
                  </Field>
                  <Field label="HTTP method(s)">
                    <MultiSelect placeholder="Any method" options={HTTP_METHODS} value={methods} disabled={pending} onChange={setMethods} />
                  </Field>
                </div>

                <div className={styles.grid2}>
                  <Field label="Maximum login attempts" hint="Must be over max failed attempts. Detects high request volumes per IP.">
                    <NumberWithUnit value={maxLogin} onChange={setMaxLogin} unit="/ min." placeholder="4" disabled={pending} />
                  </Field>
                  <Field label="Maximum failed login attempts" hint="Detects failed login attempts per IP. Origin response type required.">
                    <NumberWithUnit value={maxFailed} onChange={setMaxFailed} unit="/ min." placeholder="5" disabled={pending} />
                  </Field>
                </div>
                <div className={styles.originChecks}>
                  <span className={styles.subHint}>with origin response</span>
                  <label className={styles.checkLabel}><Checkbox checked={respCode} disabled={pending} onCheckedChange={(v) => (v ? setRespCode(true) : clearCode())} /> Code</label>
                  <label className={styles.checkLabel}><Checkbox checked={respBody} disabled={pending} onCheckedChange={(v) => (v ? setRespBody(true) : clearBody())} /> Body</label>
                  <label className={styles.checkLabel}><Checkbox checked={respHeader} disabled={pending} onCheckedChange={(v) => (v ? setRespHeader(true) : clearHeader())} /> Header</label>
                </div>

                {respCode && (
                  <Field label="HTTP response code(s)">
                    <div className={styles.removableRow}>
                      <MultiSelect placeholder="Select response codes" options={HTTP_CODES} value={codes} disabled={pending} onChange={setCodes} />
                      <IconButton variant="ghost" size="sm" aria-label="Remove response code condition" disabled={pending} onClick={clearCode}><Trash2 size={15} /></IconButton>
                    </div>
                  </Field>
                )}
                {respBody && (
                  <Field label="HTTP response body" hint="Use commas to separate key words in the response body, e.g. Unrecognized, username, password.">
                    <div className={styles.removableRow}>
                      <Input value={bodyKeywords} placeholder="username" disabled={pending} onChange={(e) => setBodyKeywords(e.target.value)} />
                      <IconButton variant="ghost" size="sm" aria-label="Remove response body condition" disabled={pending} onClick={clearBody}><Trash2 size={15} /></IconButton>
                    </div>
                  </Field>
                )}
                {respHeader && (
                  <Field label="HTTP response header">
                    <div className={styles.headerRow}>
                      <Input value={headerName} placeholder="Header name" disabled={pending} onChange={(e) => setHeaderName(e.target.value)} aria-label="Response header name" />
                      <Select value={headerOp} onValueChange={setHeaderOp} disabled={pending}>
                        <SelectTrigger aria-label="Response header operator"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {HEADER_OPERATORS.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={headerValue} placeholder="Value" disabled={pending} onChange={(e) => setHeaderValue(e.target.value)} aria-label="Response header value" />
                      <IconButton variant="ghost" size="sm" aria-label="Remove response header condition" disabled={pending} onClick={clearHeader}><Trash2 size={15} /></IconButton>
                    </div>
                  </Field>
                )}
              </>
            ) : (
              <>
                <Field label="Path">
                  <Input value={path} placeholder="/login" disabled={pending} onChange={(e) => setPath(e.target.value)} />
                </Field>
                <Field label="Payload type" hint="Indicate the data type of the request body.">
                  <Select value={payloadType} onValueChange={setPayloadType} disabled={pending}>
                    <SelectTrigger aria-label="Payload type" style={{ maxWidth: 260 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYLOAD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Account attribute name" hint="Identifies the account attribute name in the login form.">
                  <Input value={usernameExpr} placeholder="username" disabled={pending} onChange={(e) => setUsernameExpr(e.target.value)} />
                </Field>
                <Field label="Password attribute name" hint="Identifies the password attribute name in the login form.">
                  <Input value={passwordExpr} placeholder="password" disabled={pending} onChange={(e) => setPasswordExpr(e.target.value)} />
                </Field>
              </>
            )}

            <hr className={styles.divider} />
            <p className={styles.sectionTitle}>Then take an action…</p>
            <div className={styles.segment}>
              {(isFL ? FL_ACTIONS : CS_ACTIONS).map((o) => (
                <Button key={o.value} size="sm" variant={action === o.value ? undefined : 'secondary'} disabled={pending} onClick={() => setAction(o.value)}>
                  {o.label}
                </Button>
              ))}
            </div>

            {isFL && action !== 'log' && (
              <Field label="Duration">
                <NumberWithUnit value={duration} onChange={setDuration} unit="Sec." placeholder="60" disabled={pending} />
              </Field>
            )}

            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end', marginTop: 'var(--vn-space-2)' }}>
              <Button variant="secondary" onClick={back} disabled={pending}>Cancel</Button>
              <Button onClick={submit} disabled={pending || !valid}>{isEdit ? 'Save changes' : 'Create'}</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}

export default WafAccountTakeoverForm;
